import { create } from "zustand";
import {
  addEventAPI,
  deleteEventAPI,
  fetchFeaturedEventsAPI,
  fetchEventOrSpaceDetailsAPI,
  AddGiftToEventOrSpaceAPI,
  DeleteGiftFromEventOrSpaceAPI,
  EditGiftFromEventOrSpaceAPI,
} from "../Services/eventService.js";
import { fetchProfileAPI } from "../Services/userService.js";
import { fetchGiftDetailsAPI } from "../Services/giftService.js";

// Utility functions for saving and loading from localStorage
const saveToLocalStorage = (key, value) => {
  localStorage.setItem(key, JSON.stringify(value));
};
const loadFromLocalStorage = (key) => {
  const storedValue = localStorage.getItem(key);
  return storedValue ? JSON.parse(storedValue) : null;
};

// A Zustand store to manage event-related state
const useEventStore = create((set, get) => ({
  // State variables
  events: [],
  setEvents: (newEvents) => set({ events: newEvents }),

  spaces: [],
  setEvents: (newSpaces) => set({ spaces: newSpaces }),

  featuredEvents: [],
  setFeaturedEvents: (newEvents) => set({ events: newEvents }),

  selectedEventName: loadFromLocalStorage("selectedEventName") || null,
  setselectedEventName: (name) => set({ selectedEvent: name }),

  selectedEvent: loadFromLocalStorage("selectedEvent") || null, // Load the selected event from localStorage or set to null if not found
  setSelectedEvent: (event) => set({ selectedEvent: event }),

  selectedEventDetails: loadFromLocalStorage("selectedEventDetails") || null, // Load the selected event from localStorage or set to null if not found
  setSelectedEventDetails: (eventDetails) =>
    set({ selectedEventDetails: eventDetails }),

  gifts: [],
  setGifts: (newGifts) => set({ gifts: newGifts }),

  giftPrompts: [],
  setGiftPrompts: (newPrompt) => set({ giftPrompts: newPrompt }),

  selectedGift: null,
  setSelectedGift: (gift) => set({ selectedGift: gift }),

  email: "",
  setemail: (email) => set({ email: email }), // Function to update the Username.

  description: "",
  setDescription: (description) => set({ description: description }), // Function to update description.

  openComponentID: null, // ID of the currently open component
  setOpenComponentID: (id) => set({ openComponentID: id }),

  // originalEventName: loadFromLocalStorage("originalEventName") || "",
  // setOriginalEventName: (originalEventName) => set({ originalEventName: originalEventName }), // Function to update the Username.

  // Action to fetch events, organizer, and description from an API
  fetchEvents: async () => {
    try {
      const response = await fetchProfileAPI();
      // Update the 'events' state with the data received from the API
      set({ events: response.events });
      set({ spaces: response.spaces });
      set({ email: response.email });
    } catch (error) {
      console.error("Failed to fetch events:", error); // Handle errors if the API call fails
    }
  },

  // Action to Add events
  handleAddEvent: async (event) => {
    try {
      const response = await addEventAPI(event);
      if (response) {
        get().fetchEvents();
      }
    } catch (error) {
      console.error("Failed to add event:", error); // Handle errors if the API call fails
    }
  },

  // Action to delete event
  handleDeletingEventsOrSpaces: async (type, name) => {
    try {
      const response = await deleteEventAPI(type, name);
      if (response.status === 200) {
        console.log(`${name} of type ${type} has been deleted`);
        get().fetchEvents();
      }
      const data = await response.json();
    } catch (error) {
      console.error("Failed to delete event:", error); // Handle errors if the API call fails
    }
  },

  // Action to fetch Featured/Upcoming event and its description from an API
  fetchFeaturedEvents: async () => {
    try {
      const response = await fetchFeaturedEventsAPI();
      set({ featuredEvents: response.data });
    } catch (error) {
      console.error("Failed to fetch featured events:", error);
    }
  },

  // Action to select an event based on its name
  selectEvent: (type, eventName) => {
    set((state) => {
      // Find the event with the matching name from the 'events' array
      const event = state.events.find((e) => e.eventName === eventName);
      set({ selectedEventName: eventName });
      // saveToLocalStorage("selectedEventName", eventName);
      get().fetchEventOrSpaceDetails(type, eventName);

      // saveToLocalStorage("selectedEvent", event);
      // Save the selected event to localStorage

      return { selectedEvent: event }; // Update selectedEvent to the found event
    });
  },

  fetchEventOrSpaceDetails: async (type, name) => {
    // console.log("eventnamefrom store:", spaceName);
    // localStorage.setItem("originalEventName", eventName);
    // set({originalEventName: eventName})
    try {
      const response = await fetchEventOrSpaceDetailsAPI(type, name);
      const details = response.data;
      const giftIDs = details.giftID ? details.giftID.map((ids) => ids[0]) : [];

      get().fetchGifts(giftIDs);
      return details;
    } catch (error) {
      console.error("Error fetching event details:", error);
    }
  },

  // Function to fetch all Gift Details based of the GiftIDs
  fetchGifts: async (giftIDs) => {
    try {
      const gifts = await fetchGiftDetailsAPI(giftIDs);
      const mappedGifts = gifts.map((gift) => ({
        ...gift,
        categoriesTranslated: gift.categoriesTranslated || null,
      }));
      set({ gifts: mappedGifts });
    } catch (error) {
      console.error("Error fetching gifts:", error);
    }
  },

  // Function to ADD Gift from Event based on GiftID, and EventName
  handleGiftAddition: async (type, name, giftID) => {
    try {
      const response = await AddGiftToEventOrSpaceAPI(type, name, giftID);

      if (response) {
        get().fetchEventOrSpaceDetails(type, name);
      } else {
        console.error("Failed to update event data.");
      }
    } catch (error) {
      console.error("Error updating event data:", error);
    }
  },

  // Function to delete Gift from Event based on GiftID, and EventName
  handleGiftDeletion: async (type, name, giftID) => {
    try {
      const response = await DeleteGiftFromEventOrSpaceAPI(type, name, giftID);

      if (response) {
        get().fetchEventOrSpaceDetails(type, name);
      } else {
        console.error("Failed to update event data.");
      }
    } catch (error) {
      console.error("Error updating event data:", error);
    }
  },

  handleEdit: async (type, name, updatedData) => {
    try {
      const response = await EditGiftFromEventOrSpaceAPI(
        type,
        name,
        updatedData
      );

      if (response) {
        // Update the specific event/space in the local store state to reflect changes immediately
        set((state) => {
          const collection = type === "events" ? "events" : "spaces";
          const items = state[collection] || [];
          const updatedItems = items.map((item) => {
            const itemName = type === "events" ? item.eventName : item.spaceName;
            if (itemName === name) {
              // Merge existing item with updated data
              // If the response contains the full updated object, we could use that too,
              // but updatedData contains the changes we just sent.
              // We also check if response.updatedData exists and merge it if so.
              const apiUpdates = response.updatedData || {};
              return { ...item, ...updatedData, ...apiUpdates };
            }
            return item;
          });
          return { [collection]: updatedItems };
        });

        get().fetchEventOrSpaceDetails(type, name);
        return response;
      } else {
        console.error("Failed to update event data.");
      }
    } catch (error) {
      console.error("Error updating event data:", error);
    }
  },
}));

export default useEventStore;
